/**
 * Apps Query Hooks
 * 
 * React Query hooks for fetching application data.
 * 
 * @module hooks/queries/useApps
 * 
 * @example
 * // In a component:
 * const { data: apps, isLoading, error } = useMyApps();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appsService } from '../../services';
import { notify } from '../../shared/services';
import { CreateAppDto, UpdateAppDto } from '../../types';
import { queryKeys } from './queryKeys';

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch current user's authorized apps
 * 
 * This is the primary hook for the dashboard.
 * Returns only apps the current user has access to.
 * 
 * @returns Query result with apps array
 * 
 * @example
 * const { data: apps, isLoading, isError } = useMyApps();
 * 
 * if (isLoading) return <Skeleton />;
 * if (isError) return <Error />;
 * return <AppsList apps={apps} />;
 */
export const useMyApps = () => {
  return useQuery({
    queryKey: queryKeys.apps.myApps(),
    queryFn: () => appsService.getMyApps(),
  });
};

/**
 * Hook to fetch all apps (admin only)
 * 
 * @returns Query result with all apps
 */
export const useAllApps = () => {
  return useQuery({
    queryKey: queryKeys.apps.lists(),
    queryFn: () => appsService.getAll(),
  });
};

/**
 * Hook to fetch active apps only
 * 
 * @returns Query result with active apps
 */
export const useActiveApps = () => {
  return useQuery({
    queryKey: queryKeys.apps.active(),
    queryFn: () => appsService.getActive(),
  });
};

/**
 * Hook to fetch a single app by ID
 * 
 * @param id - App ID
 * @returns Query result with app data
 */
export const useApp = (id: number) => {
  return useQuery({
    queryKey: queryKeys.apps.detail(id),
    queryFn: () => appsService.getById(id),
    enabled: !!id, // Only fetch if ID is provided
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new app
 * 
 * Automatically invalidates apps queries on success.
 * 
 * @returns Mutation for creating apps
 * 
 * @example
 * const { mutate: createApp, isPending } = useCreateApp();
 * 
 * createApp({ appName: 'New App', appUrl: 'https://...' });
 */
export const useCreateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppDto) => appsService.create(data),
    onSuccess: () => {
      // Invalidate all apps queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.all });
    },
  });
};

/**
 * Hook to update an existing app
 * 
 * @returns Mutation for updating apps
 */
export const useUpdateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAppDto }) =>
      appsService.update(id, data),
    onSuccess: (updatedApp) => {
      // Update the specific app in cache
      queryClient.setQueryData(
        queryKeys.apps.detail(updatedApp.id),
        updatedApp
      );
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.myApps() });
    },
  });
};

/**
 * Hook to delete an app
 * 
 * @returns Mutation for deleting apps
 */
export const useDeleteApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => appsService.delete(id),
    onSuccess: () => {
      // Invalidate all apps queries
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.all });
    },
  });
};

/**
 * Hook to toggle app active status
 * 
 * @returns Mutation for toggling app status
 */
export const useToggleAppActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => appsService.toggleActive(id),
    onSuccess: (updatedApp) => {
      // Update cache with new status
      queryClient.setQueryData(
        queryKeys.apps.detail(updatedApp.id),
        updatedApp
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.myApps() });
    },
  });
};

/**
 * Hook to toggle hub app favorite (current user).
 * Invalidates my-apps so dashboard refetches with updated isFavorite.
 */
export const useToggleHubAppFavorite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (appId: number) => appsService.toggleHubAppFavorite(appId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apps.myApps() });
      notify.success(data.isFavorite ? 'Added to favorites' : 'Removed from favorites');
    },
  });
};

